// 주문 작성(체크아웃) 화면.
// 작업계획서 4.5: 포장/배달 선택, 희망 수령 시간, 배달지/지역/최소금액/배달비, 영업시간 재검증, 현장 결제 안내.
// 금액/항목은 서버가 본인 카트에서 재구성하므로, 본 화면은 "선택값(유형/슬롯/주소/메모)" 만 보내고 표시는 카트 기준으로 한다.
//
// 손님 카피 톤([[feedback-customer-copy-tone]]): 부정/판단적 표현 회피. 현장 결제 안내를 명확히 노출.

import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as PortOne from "@portone/browser-sdk/v2";
import {
  COUPON_TYPE,
  ORDER_TYPE,
  WEEKDAY_LABEL_KO,
  computeCouponDiscount,
  couponKind,
  formatCartLineLabel,
  formatCouponBenefit,
  formatWeightG,
  formatCouponConditions,
  isCouponMinOrderMet,
  isCouponOrderTypeAllowed,
  isDeliveryAddressAllowed,
  isPointsUseAmountValid,
  isWithinDeliveryWindow,
  maxUsablePoints,
  narrowReservationDaysToDeliveryWindow,
  pointsUsePolicyOf,
  portoneCustomerId,
  roundKrwToTen,
  stablePgPaymentId,
  PAYMENT_METHOD_TYPE,
  PAYMENT_METHOD_TYPE_LABEL_KO,
  allowedPaymentMethodTypes,
  settlementPaymentMethodType,
  type CartItemDto,
  type OrderDto,
  type OrderType,
  type PaymentMethodType,
  type UnitDto,
} from "@oo/shared";
import { ApiError } from "@/api/client.ts";
import { useCart } from "@/features/cart/useCart.ts";
import { useUnits } from "@/features/catalog/useUnits.ts";
import { useStoreSettings } from "@/features/operations/useStoreSettings.ts";
import { listAddresses } from "@/features/operations/operationsApi.ts";
import { getCoupons } from "@/features/coupons/couponsApi.ts";
import { getPoints } from "@/features/points/pointsApi.ts";
import { formatPoints } from "@oo/shared";
import { getComplimentState, selectCompliment } from "@/features/compliment/complimentApi.ts";
import { confirmDirectPayment, createOrder } from "@/features/orders/ordersApi.ts";
import {
  NATIVE_PAYMENT_APP_SCHEME,
  clearPendingDirectPayment,
  directPaymentNoticeUrls,
  directPaymentRedirectUrl,
  rememberPendingDirectPayment,
} from "@/features/payments/directPayment.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { isNativeApp } from "@/shared/platform.ts";
import AddressQuickAddModal from "@/features/operations/AddressQuickAddModal.tsx";
import { Button } from "@/shared/ui";

// 포트원 공개 식별자(클라 노출 가능). 일반(즉시) 카드결제 결제창(requestPayment)에 쓴다.
//  - 일반결제 채널: 별도 일반결제(인증결제) 채널이 발급되면 VITE_PORTONE_CHANNEL_KEY_ONETIME 로 분리하고,
//    없으면 기존 채널로 폴백한다.
const PORTONE_STORE_ID = import.meta.env.VITE_PORTONE_STORE_ID as string | undefined;
const PORTONE_ONETIME_CHANNEL_KEY = (import.meta.env.VITE_PORTONE_CHANNEL_KEY_ONETIME ??
  import.meta.env.VITE_PORTONE_CHANNEL_KEY) as string | undefined;

// 수령 시간 선택 모드.
//  - asap   : 지금 가능한 시간에 수령 (영업 중일 때만 선택 가능)
//  - reserve: 예약 슬롯(일자+시간)을 골라 수령
type PickupMode = "asap" | "reserve";

// 주문/체크아웃 화면 (포장·배달, 예약슬롯, 할인·적립, 현장결제).
export default function CheckoutPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const cart = useCart();
  const units = useUnits();
  const ops = useStoreSettings();
  // 일반(즉시) 카드결제 결제창에 넘길 구매자 식별/이름(포트원 customer).
  const userId = useAuthStore((s) => s.session?.user.id ?? null);
  const displayName = useAuthStore((s) => s.session?.user.displayName ?? "");

  // 장바구니의 주문방식 모달에서 넘어온 고정 주문방식. 있으면 체크아웃에선 토글 없이 표시만 한다(직접 진입/새로고침이면 null → 기존 토글).
  const rawOrderType = (location.state as { orderType?: OrderType } | null)?.orderType;
  const fixedOrderType =
    rawOrderType === ORDER_TYPE.PICKUP || rawOrderType === ORDER_TYPE.DELIVERY
      ? rawOrderType
      : null;
  const typeFixed = fixedOrderType !== null;
  const [type, setType] = useState<OrderType>(fixedOrderType ?? ORDER_TYPE.PICKUP);
  const [pickupMode, setPickupMode] = useState<PickupMode>("asap");
  const [selectedDate, setSelectedDate] = useState<string>("");
  // 수령 시각은 [시]/[분] 두 칸으로 나눠 선택한다(작업지시).
  // 서버에는 합친 'HH:MM' 문자열(selectedTime)로 전달한다.
  const [selectedHour, setSelectedHour] = useState<string>("");
  const [selectedMinute, setSelectedMinute] = useState<string>("");
  const [addressId, setAddressId] = useState<string>("");
  const [memo, setMemo] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // 주문하기 전 주문내역 요약을 한 번 더 확인받는 모달(개선 2).
  const [confirmOpen, setConfirmOpen] = useState(false);
  // 주문 페이지를 벗어나지 않고 배송지를 추가하기 위한 인라인 모달 (작업지시 11).
  const [addrModalOpen, setAddrModalOpen] = useState(false);
  // 적용 쿠폰 선택 (할인 1장 + 배달비무료 1장).
  const [discountCouponId, setDiscountCouponId] = useState<string>("");
  const [deliveryCouponId, setDeliveryCouponId] = useState<string>("");
  // 자동 적용 후 사용자가 직접 쿠폰을 바꿨는지 추적한다.
  //  - 자동 적용은 "사용자가 아직 손대지 않았을 때"만 동작해, 직접 '미사용'을 골라도 다시 덮어쓰지 않게 한다.
  const [discountTouched, setDiscountTouched] = useState(false);
  const [deliveryTouched, setDeliveryTouched] = useState(false);
  // 사용할 적립금(원).
  const [pointsInput, setPointsInput] = useState<number>(0);
  // 주문 확인 모달의 안내 확인 체크박스(개선 3). 미체크 상태로 확인 누르면 빨갛게 강조.
  const [agreed, setAgreed] = useState(false);
  const [agreeError, setAgreeError] = useState(false);
  // 칭찬하고 할인받기(개선 9~12) 칸 펼침 여부.
  const [complimentExpanded, setComplimentExpanded] = useState(false);
  // 결제 수단. 기본값은 포장=현장, 배달=SMS 결제요청(정산 가능한 수단). 카드 결제는 손님이 직접 선택한다.
  const [paymentMethodType, setPaymentMethodType] = useState<PaymentMethodType>(
    PAYMENT_METHOD_TYPE.ONSITE,
  );

  // 배달 주문일 때만 주소록 조회.
  const addressesQuery = useQuery({
    queryKey: ["addresses"],
    queryFn: listAddresses,
    enabled: type === ORDER_TYPE.DELIVERY,
  });

  // 기본 배송지 자동 선택: 아직 선택이 없고 기본 배송지가 지정돼 있으면 그 주소를 미리 고른다.
  //  - 사용자가 직접 다른 주소로 바꾸면(addressId 가 채워지면) 다시 덮어쓰지 않는다.
  useEffect(() => {
    if (addressId) {
      return;
    }
    const def = addressesQuery.data?.find((a) => a.isDefault);
    if (def) {
      setAddressId(def.id);
    }
  }, [addressesQuery.data, addressId]);

  // 보유 쿠폰 (사용 가능만 선택지로 노출).
  const couponsQuery = useQuery({ queryKey: ["coupons"], queryFn: getCoupons });
  // 사용 가능 쿠폰은 종류(할인/배달)에 더해 사용 조건(주문 방식)도 만족하는 것만 노출한다.
  const usableDiscountCoupons = useMemo(
    () =>
      (couponsQuery.data ?? []).filter(
        (c) =>
          c.usable &&
          couponKind(c.type) === "discount" &&
          isCouponOrderTypeAllowed(c.applicableOrderType, type),
      ),
    [couponsQuery.data, type],
  );
  const usableDeliveryCoupons = useMemo(
    () =>
      (couponsQuery.data ?? []).filter(
        (c) =>
          c.usable &&
          c.type === COUPON_TYPE.FREE_DELIVERY &&
          isCouponOrderTypeAllowed(c.applicableOrderType, type),
      ),
    [couponsQuery.data, type],
  );

  // 보유 적립금 잔액.
  const pointsQuery = useQuery({ queryKey: ["points"], queryFn: getPoints });

  // 칭찬하고 할인받기(개선 9~12). 운영설정에서 켜져 있을 때만 조회한다. 주문 유형별 후보 개수가 달라 type 을 키에 포함.
  const complimentEnabledSetting = ops.settings?.complimentEnabled ?? false;
  const complimentQuery = useQuery({
    queryKey: ["compliment", type],
    queryFn: () => getComplimentState(type),
    enabled: complimentEnabledSetting,
  });
  const complimentState = complimentQuery.data;
  const complimentSelectMut = useMutation({
    mutationFn: (text: string) => selectCompliment(text),
    onSuccess: () => {
      // 선택 후 잠금 상태로 갱신(고른 칭찬/할인 표시).
      void qc.invalidateQueries({ queryKey: ["compliment"] });
    },
  });

  const view = cart.view;
  // 실중량 관리 대상품(중량 변동 폭이 큰 정육 부위)이 담겼는지. 담겼으면 주문 시점에 금액을 확정할 수 없어
  // 카드 즉시결제를 막고 정산 가능한 수단(포장=현장 / 배달=SMS)만 남긴다.
  const weightAdjustableItems = useMemo(
    () => (view?.items ?? []).filter((it) => it.productSnapshot.weightAdjustable),
    [view],
  );
  const hasWeightAdjustableItem = weightAdjustableItems.length > 0;
  // 실중량 대상은 아니지만 무게로 파는 상품(자투리 포함 안내 대상).
  const weightUnitItems = useMemo(
    () =>
      (view?.items ?? []).filter(
        (it) => !it.productSnapshot.weightAdjustable && (it.productSnapshot.baseGramsG ?? 0) > 0,
      ),
    [view],
  );

  // 현재 주문 방식이 허용하는 결제수단. 실중량 대상품이 담기면 정산 가능한 수단 하나만 남는다.
  const allowedMethods = useMemo(
    () => allowedPaymentMethodTypes(type, { hasWeightAdjustableItem }),
    [type, hasWeightAdjustableItem],
  );

  // 주문 방식/장바구니가 바뀌어 현재 선택이 허용되지 않으면 정산 가능한 기본 수단으로 되돌린다.
  useEffect(() => {
    if (allowedMethods.includes(paymentMethodType)) return;
    setPaymentMethodType(settlementPaymentMethodType(type));
  }, [type, allowedMethods, paymentMethodType]);

  const settings = ops.settings;
  const openStatus = ops.openStatus;
  // 다음날 배달 전용(운영설정). 켜져 있고 배달 주문이면 수령 시간 선택 없이 "다음날 오전 배송"으로 고정된다.
  const nextDayDeliveryOnly = settings?.nextDayDeliveryOnly ?? false;
  const isNextDayDelivery = nextDayDeliveryOnly && type === ORDER_TYPE.DELIVERY;
  // 배달 주문이면 예약 슬롯을 배달 주문 가능 시간(개선 2)으로 좁힌다. 포장/미설정이면 그대로.
  const reservationDays = useMemo(() => {
    const days = ops.reservationDays;
    if (type === ORDER_TYPE.DELIVERY && settings) {
      return narrowReservationDaysToDeliveryWindow(days, settings);
    }
    return days;
  }, [ops.reservationDays, type, settings]);

  // 선택된 날짜의 슬롯 목록('HH:MM' 배열).
  const slotsForSelectedDate = useMemo(() => {
    return reservationDays.find((d) => d.yyyyMmDd === selectedDate)?.slots ?? [];
  }, [reservationDays, selectedDate]);

  // 슬롯에서 선택 가능한 '시' 목록(중복 제거, 오름차순).
  const availableHours = useMemo(() => {
    const hours = new Set<string>();
    for (const slot of slotsForSelectedDate) {
      hours.add(slot.slice(0, 2));
    }
    return [...hours].sort();
  }, [slotsForSelectedDate]);

  // 선택한 '시'에 대해 고를 수 있는 '분' 목록.
  const availableMinutes = useMemo(() => {
    if (!selectedHour) return [] as string[];
    const minutes = slotsForSelectedDate
      .filter((slot) => slot.slice(0, 2) === selectedHour)
      .map((slot) => slot.slice(3, 5));
    return [...new Set(minutes)].sort();
  }, [slotsForSelectedDate, selectedHour]);

  // 서버 전송/검증용 합산 시각. 시·분이 모두 선택돼야 유효.
  const selectedTime = selectedHour && selectedMinute ? `${selectedHour}:${selectedMinute}` : "";

  // 배달 주문이면 "지금" 시각이 배달 주문 가능 시간(개선 2) 안인지도 확인한다(KST 기준).
  const deliveryAsapOk = useMemo(() => {
    if (type !== ORDER_TYPE.DELIVERY || !settings) return true;
    const d = new Date(Date.now() + 9 * 60 * 60 * 1000);
    const hhmm = `${String(d.getUTCHours()).padStart(2, "0")}:${String(d.getUTCMinutes()).padStart(2, "0")}`;
    return isWithinDeliveryWindow(settings, hhmm);
  }, [type, settings]);

  // 영업 중이 아니거나(배달이면 배달 시간대 밖이면) ASAP 를 막고 예약 모드로 강제한다.
  const canAsap = openStatus?.isOpen === true && deliveryAsapOk;
  const effectiveMode: PickupMode = canAsap ? pickupMode : "reserve";

  // 선택된 배송지.
  const selectedAddress = useMemo(
    () => addressesQuery.data?.find((a) => a.id === addressId) ?? null,
    [addressesQuery.data, addressId],
  );

  // 합계 계산 (표시용 - 서버가 최종 계산). 배달비는 배달 주문에서만 더한다.
  const subtotal = view?.totalKrw ?? 0;
  // 주문 전체 총 중량(g) = 각 줄의 기본중량*수량 + 추가옵션 중량 합계.
  const totalWeightG = (view?.items ?? []).reduce((sum, it) => sum + (it.totalWeightG ?? 0), 0);
  const baseDeliveryFee = type === ORDER_TYPE.DELIVERY && settings ? settings.deliveryFeeKrw : 0;

  // 가장 할인이 큰 할인 쿠폰 id (자동 적용 대상). 최소 주문금액을 충족하는 쿠폰 중 실제 할인액이 최대인 것.
  const bestDiscountCouponId = useMemo(() => {
    let bestId = "";
    let bestAmount = 0;
    for (const c of usableDiscountCoupons) {
      if (!isCouponMinOrderMet(c.minOrderKrw, subtotal)) continue;
      const amount = computeCouponDiscount(c.type, c.value, subtotal, c.maxDiscountKrw);
      if (amount > bestAmount) {
        bestAmount = amount;
        bestId = c.id;
      }
    }
    return bestId;
  }, [usableDiscountCoupons, subtotal]);

  // 자동 적용(개선 1): 사용자가 아직 손대지 않았다면 가장 할인이 큰 할인 쿠폰을 기본 선택한다.
  useEffect(() => {
    if (!discountTouched && !discountCouponId && bestDiscountCouponId) {
      setDiscountCouponId(bestDiscountCouponId);
    }
  }, [discountTouched, discountCouponId, bestDiscountCouponId]);

  // 주문 방식이 바뀌어 선택한 할인 쿠폰이 그 방식에서 못 쓰게 되면 선택을 비운다(어긋난 선택 방지).
  useEffect(() => {
    if (discountCouponId && !usableDiscountCoupons.some((c) => c.id === discountCouponId)) {
      setDiscountCouponId("");
    }
  }, [usableDiscountCoupons, discountCouponId]);

  // 배달 주문이면 배달비 무료 쿠폰도 자동 적용(혜택이 동일하므로 최소금액 충족분 우선, 없으면 첫 장).
  useEffect(() => {
    if (type !== ORDER_TYPE.DELIVERY || deliveryTouched || deliveryCouponId) return;
    if (usableDeliveryCoupons.length === 0) return;
    const pick =
      usableDeliveryCoupons.find((c) => isCouponMinOrderMet(c.minOrderKrw, subtotal)) ??
      usableDeliveryCoupons[0];
    if (pick) {
      setDeliveryCouponId(pick.id);
    }
  }, [type, deliveryTouched, deliveryCouponId, usableDeliveryCoupons, subtotal]);

  // 선택 쿠폰 적용 (표시용). 할인은 subtotal 기준, 배달비무료는 배달 주문에서만.
  const selectedDiscountCoupon = useMemo(
    () => usableDiscountCoupons.find((c) => c.id === discountCouponId) ?? null,
    [usableDiscountCoupons, discountCouponId],
  );
  // 선택한 할인 쿠폰의 최소 주문금액 충족 여부. 미충족이면 적용/전송하지 않는다.
  const discountMinOrderMet =
    !selectedDiscountCoupon || isCouponMinOrderMet(selectedDiscountCoupon.minOrderKrw, subtotal);
  const discount =
    selectedDiscountCoupon && discountMinOrderMet
      ? computeCouponDiscount(
          selectedDiscountCoupon.type,
          selectedDiscountCoupon.value,
          subtotal,
          selectedDiscountCoupon.maxDiscountKrw,
        )
      : 0;
  // 선택한 배달비 쿠폰의 최소 주문금액 충족 여부.
  const selectedDeliveryCoupon = useMemo(
    () => usableDeliveryCoupons.find((c) => c.id === deliveryCouponId) ?? null,
    [usableDeliveryCoupons, deliveryCouponId],
  );
  const deliveryMinOrderMet =
    !selectedDeliveryCoupon || isCouponMinOrderMet(selectedDeliveryCoupon.minOrderKrw, subtotal);
  const deliveryCouponApplied =
    type === ORDER_TYPE.DELIVERY && !!selectedDeliveryCoupon && deliveryMinOrderMet;
  const deliveryFee = deliveryCouponApplied ? 0 : baseDeliveryFee;
  // 칭찬 할인(개선 9~12). 잠긴 선택이 있고 아직 다른 주문에 적용되지 않았을 때만 이 주문에 적용된다(서버 기준과 일치).
  const complimentDiscount =
    complimentState && complimentState.locked && !complimentState.appliedToOrder
      ? complimentState.discountKrw
      : 0;
  const payableBeforePoints = Math.max(0, subtotal - discount - complimentDiscount + deliveryFee);

  // 적립금 사용. 잔액/결제예정액 한도 + 운영설정의 최소사용·단위 정책(개선항목 16)을 반영한다.
  const pointsBalance = pointsQuery.data?.balance ?? 0;
  const pointsPolicy = settings ? pointsUsePolicyOf(settings) : { minKrw: 0, unitKrw: 1 };
  // 정책을 만족하는 최대 사용액(전액 사용/상한 표시용).
  const usablePoints = maxUsablePoints(pointsBalance, payableBeforePoints, pointsPolicy);
  // 입력값이 0이거나 (한도 이내 + 최소사용/단위 충족) 일 때만 유효.
  const pointsInputValid =
    pointsInput === 0 ||
    (pointsInput <= Math.min(pointsBalance, payableBeforePoints) &&
      isPointsUseAmountValid(pointsInput, pointsPolicy));
  const pointsUsed = pointsInputValid ? Math.max(0, pointsInput) : 0;
  const total = payableBeforePoints - pointsUsed;
  // 실중량 정산으로 최종 결제액이 흔들리는 범위.
  //  - 금액이 변하는 건 "실중량 정산 대상" 라인뿐이다. 일반 상품/배달비는 그대로이므로
  //    총 합계에 ±10% 를 걸면 범위가 과장된다(대상 상품 라인 합계에만 ±10%).
  //  - 그 뒤 단계(정률 쿠폰 재계산 -> 칭찬할인 보정 -> 배달비 -> 적립금 클램프)는
  //    서버 정산(computeWeightSettlement)과 같은 순서로 다시 태워 최종 결제액을 만든다.
  const weightAdjustableSubtotal = useMemo(
    () => weightAdjustableItems.reduce((sum, it) => sum + it.lineTotalKrw, 0),
    [weightAdjustableItems],
  );
  // 상품합계가 nextSubtotal 로 확정됐을 때의 최종 결제액.
  const totalAtSubtotal = useCallback(
    (nextSubtotal: number) => {
      const nextDiscount =
        selectedDiscountCoupon && discountMinOrderMet
          ? Math.min(
              nextSubtotal,
              computeCouponDiscount(
                selectedDiscountCoupon.type,
                selectedDiscountCoupon.value,
                nextSubtotal,
                selectedDiscountCoupon.maxDiscountKrw,
              ),
            )
          : 0;
      // 칭찬 할인(정액)은 (상품합계-쿠폰할인) 범위로 보정.
      const nextCompliment = Math.min(Math.max(0, nextSubtotal - nextDiscount), complimentDiscount);
      const nextPayable = Math.max(0, nextSubtotal - nextDiscount - nextCompliment + deliveryFee);
      // 적립금은 결제예정액을 넘겨 쓸 수 없다(초과분은 서버가 환불).
      return Math.max(0, nextPayable - Math.min(pointsUsed, nextPayable));
    },
    [selectedDiscountCoupon, discountMinOrderMet, complimentDiscount, deliveryFee, pointsUsed],
  );
  const fixedSubtotal = subtotal - weightAdjustableSubtotal;
  const minPayment = totalAtSubtotal(fixedSubtotal + roundKrwToTen(weightAdjustableSubtotal * 0.9));
  const maxPayment = totalAtSubtotal(fixedSubtotal + roundKrwToTen(weightAdjustableSubtotal * 1.1));

  // 카트가 비었거나 사용 불가/품절 항목이 있으면 주문 진입 자체를 막는다.
  const hasBlockingItems = !!view && (view.unavailableCount > 0 || view.soldOutCount > 0);
  const isEmpty = !view || view.items.length === 0;

  // 배달 검증(클라이언트 사전 안내 - 서버가 최종 판정).
  const deliveryMinOk =
    type !== ORDER_TYPE.DELIVERY || !settings ? true : subtotal >= settings.deliveryMinOrderKrw;
  const deliveryAreaOk =
    type !== ORDER_TYPE.DELIVERY || !settings || !selectedAddress
      ? true
      : isDeliveryAddressAllowed(
          `${selectedAddress.addressLine1} ${selectedAddress.addressLine2 ?? ""}`.trim(),
          settings.deliveryAreaKeywords,
        );

  // 주문 버튼 활성 조건.
  //  - 다음날 배달 전용 주문은 수령 시간 선택이 없으므로 시간 준비조건을 통과로 둔다.
  const reserveReady = isNextDayDelivery
    ? true
    : effectiveMode === "asap"
      ? canAsap
      : !!selectedDate && !!selectedTime;
  const deliveryReady =
    type !== ORDER_TYPE.DELIVERY || (!!addressId && deliveryMinOk && deliveryAreaOk);
  const canSubmit =
    !isEmpty &&
    !hasBlockingItems &&
    reserveReady &&
    deliveryReady &&
    pointsInputValid &&
    !submitting;

  // 주문 요약/확인 모달의 결제 안내 문구(결제수단별).
  const paymentSummaryNote =
    paymentMethodType === PAYMENT_METHOD_TYPE.INAPP_DIRECT
      ? "주문 시 카드 결제창에서 표시 금액으로 바로 결제됩니다."
      : paymentMethodType === PAYMENT_METHOD_TYPE.SMS_REQUEST
        ? "준비 완료 시 결제 안내 문자(SMS)를 보내드립니다."
        : "포장 수령 시 현장에서 결제됩니다.";

  // 주문 후 공통 처리: 주문 상세로 이동 + 관련 캐시 무효화.
  //  - 캐시 무효화를 await 하면 그 사이 카트 쿼리가 빈 상태로 갱신되어 이 화면이 빈 화면으로 잠깐 바뀌는 깜빡임이 있었다.
  //    화면 전환을 먼저 하고, 카트/쿠폰/적립금 캐시는 백그라운드로 무효화한다.
  function goToOrderDetail(orderId: string) {
    navigate(`/me/orders/${orderId}`, { replace: true, state: { justCreated: true } });
    void qc.invalidateQueries({ queryKey: ["cart", "view"] });
    void qc.invalidateQueries({ queryKey: ["coupons"] });
    void qc.invalidateQueries({ queryKey: ["points"] });
    void qc.invalidateQueries({ queryKey: ["compliment"] });
  }

  // 일반(즉시) 카드결제: 생성된 주문에 대해 포트원 결제창을 띄운다.
  //  - 모바일은 redirectUrl 로 전체화면 리다이렉트되어 이 함수 이후 코드는 실행되지 않고, 복귀는 주문 상세가 처리한다.
  //  - 데스크톱은 팝업/iframe 으로 즉시 반환되므로 결과를 받아 서버 확인(confirm-direct)까지 진행한다.
  //  - 주문은 이미 생성됐으므로 이 함수는 절대 throw 하지 않는다(throw 하면 체크아웃에 빈 카트로 갇힌다).
  //    실패/취소 시에도 항상 주문 상세로 이동하고, 실제 오류 사유는 state(directPayError)로 넘겨 상세에서 보여준다.
  async function startDirectPayment(order: OrderDto): Promise<void> {
    let directPayError: string | null = null;
    rememberPendingDirectPayment(order.id);
    try {
      const res = await PortOne.requestPayment({
        storeId: PORTONE_STORE_ID!,
        channelKey: PORTONE_ONETIME_CHANNEL_KEY!,
        // 안정 paymentId(영문/숫자만). 서버 확인/환불이 같은 식별자를 쓴다.
        paymentId: stablePgPaymentId(order.id),
        orderName: `주문 ${order.orderNo}`,
        totalAmount: order.totalKrw,
        currency: "KRW",
        payMethod: "CARD",
        customer: userId
          ? { customerId: portoneCustomerId(userId), fullName: displayName.trim() || undefined }
          : undefined,
        redirectUrl: directPaymentRedirectUrl(order.id),
        // 네이티브 앱: 카드사 앱(ISP/앱카드) 인증 후 우리 앱으로 복귀할 커스텀 스킴(AndroidManifest=openchuksan).
        appScheme: isNativeApp ? NATIVE_PAYMENT_APP_SCHEME : undefined,
        noticeUrls: directPaymentNoticeUrls(),
      });
      // 여기 도달 = 팝업/iframe 즉시 반환(리다이렉트 아님). res 가 undefined 면 사용자가 창을 닫음.
      if (res?.code) {
        // PG/SDK 가 돌려준 실패 사유를 그대로 노출(디버깅 + 손님 안내).
        console.error("[payment] requestPayment 실패", res);
        directPayError = `결제가 완료되지 않았습니다: ${res.pgMessage ?? res.message ?? res.code}`;
      } else if (res) {
        // 결제창 성공 응답 → 서버가 포트원으로 최종 검증한다.
        try {
          await confirmDirectPayment(order.id);
          clearPendingDirectPayment(order.id);
        } catch (e) {
          directPayError =
            e instanceof ApiError
              ? e.message
              : "결제 확인에 실패했습니다. 주문 상세에서 다시 시도해 주세요.";
        }
      }
    } catch (err) {
      // 결제창 자체를 띄우지 못한 경우(설정/채널/네트워크 등). 실제 메시지를 보존해 원인 파악을 돕는다.
      console.error("[payment] requestPayment 예외", err);
      directPayError = `결제창을 열지 못했습니다: ${err instanceof Error ? err.message : String(err)}`;
    }
    // 성공/실패/취소 모두 주문 상세로 이동(미결제면 상세에서 재시도 안내 + 오류 사유 표시).
    navigate(`/me/orders/${order.id}`, {
      replace: true,
      state: { justCreated: true, directPayError },
    });
    void qc.invalidateQueries({ queryKey: ["cart", "view"] });
    void qc.invalidateQueries({ queryKey: ["coupons"] });
    void qc.invalidateQueries({ queryKey: ["points"] });
    void qc.invalidateQueries({ queryKey: ["compliment"] });
  }

  async function onSubmit() {
    if (!canSubmit) return;
    // 일반(즉시) 카드결제는 0원 주문을 결제창에서 처리할 수 없다(전액 적립/쿠폰 등). 다른 수단을 안내.
    if (paymentMethodType === PAYMENT_METHOD_TYPE.INAPP_DIRECT && total <= 0) {
      setErrorMsg(
        "결제 금액이 0원이라 카드 결제가 필요하지 않습니다. 다른 결제 수단을 선택해 주세요.",
      );
      setConfirmOpen(false);
      return;
    }
    setSubmitting(true);
    setErrorMsg(null);
    try {
      const order = await createOrder({
        type,
        // 다음날 배달 전용 주문은 시간 선택이 없으므로 슬롯을 비운다(서버도 무시).
        desiredDate: isNextDayDelivery ? null : effectiveMode === "reserve" ? selectedDate : null,
        desiredTime: isNextDayDelivery ? null : effectiveMode === "reserve" ? selectedTime : null,
        addressId: type === ORDER_TYPE.DELIVERY ? addressId : null,
        customerMemo: memo.trim() ? memo.trim() : null,
        // 최소 주문금액/주문방식 조건 미충족 쿠폰은 서버가 거절하므로, 유효(선택 가능 목록에 있는)할 때만 보낸다.
        discountCouponId:
          selectedDiscountCoupon && discountMinOrderMet ? selectedDiscountCoupon.id : null,
        deliveryCouponId: deliveryCouponApplied ? deliveryCouponId : null,
        pointsUsed: pointsUsed > 0 ? pointsUsed : 0,
        paymentMethodType,
      });
      // 일반(즉시) 카드결제는 주문 생성 후 결제창으로 즉시 결제한다.
      //  - submitting 을 false 로 되돌리지 않아(전환/결제창 동안 버튼 로딩 유지) goToOrderDetail 까지 이어간다.
      if (paymentMethodType === PAYMENT_METHOD_TYPE.INAPP_DIRECT) {
        await startDirectPayment(order);
        return;
      }
      // 그 외(현장/SMS/등록카드): 바로 주문 상세로 이동.
      goToOrderDetail(order.id);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : "주문을 접수하지 못했습니다. 잠시 후 다시 시도해 주세요.";
      setErrorMsg(msg);
      setSubmitting(false);
      // 실패 시 확인 모달을 닫아 주문 화면에서 사유를 보고 다시 시도하게 한다.
      setConfirmOpen(false);
    }
  }

  // 로딩.
  if (cart.isLoading || ops.isLoading) {
    return (
      <main className="mx-auto flex max-w-screen-sm flex-col gap-4 px-4 py-8">
        <h1 className="text-2xl font-bold">주문하기</h1>
        <div className="h-40 animate-pulse rounded-lg bg-ink-100" />
      </main>
    );
  }

  // 빈 카트/차단 항목 - 카트로 돌려보낸다.
  if (isEmpty || hasBlockingItems) {
    return (
      <main className="mx-auto flex max-w-screen-sm flex-col gap-4 px-4 py-8">
        <h1 className="text-2xl font-bold">주문하기</h1>
        <div className="rounded-lg border border-warning-600/30 bg-warning-50 p-4 text-sm text-warning-700">
          <p className="font-semibold">장바구니를 먼저 확인해 주세요.</p>
          <p className="mt-1 text-xs">
            {isEmpty
              ? "담긴 상품이 없습니다."
              : "지금 주문하기 어려운 상품이 담겨 있습니다. 장바구니에서 정리해 주세요."}
          </p>
        </div>
        <Button type="button" onClick={() => navigate("/cart")}>
          장바구니로 가기
        </Button>
      </main>
    );
  }

  const deliveryEnabled = settings?.deliveryEnabled ?? false;

  return (
    <main className="mx-auto flex w-full max-w-screen-sm flex-col gap-5 px-4 py-6 pb-[calc(10rem+var(--safe-bottom))]">
      <h1 className="text-2xl font-bold">주문하기</h1>

      {/* 1. 주문 방식 - 장바구니에서 선택해 넘어왔으면 고정 표시(변경=장바구니로), 아니면 토글 */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">주문 방식</h2>
        {typeFixed ? (
          <div className="flex items-center justify-between rounded-lg border border-brand-600 bg-brand-50 p-3">
            <div className="min-w-0">
              <p className="text-base font-semibold text-brand-800">
                {type === ORDER_TYPE.DELIVERY
                  ? nextDayDeliveryOnly
                    ? "내일 배달"
                    : "배달"
                  : "포장"}
              </p>
              <p className="text-xs text-brand-700">
                {type === ORDER_TYPE.DELIVERY
                  ? nextDayDeliveryOnly
                    ? "내일 오전 배송"
                    : "배송지로 수령"
                  : "매장에서 수령"}
              </p>
            </div>
            <button
              type="button"
              onClick={() => navigate("/cart")}
              className="shrink-0 text-xs font-medium text-brand-700 underline"
            >
              변경
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            <TypeButton
              active={type === ORDER_TYPE.PICKUP}
              label="포장"
              sub="매장에서 수령"
              onClick={() => setType(ORDER_TYPE.PICKUP)}
            />
            <TypeButton
              active={type === ORDER_TYPE.DELIVERY}
              // 다음날 배달 전용이면 버튼 이름도 '내일 배달'로 바꿔 익일 배송임을 알린다.
              label={nextDayDeliveryOnly ? "내일 배달" : "배달"}
              sub={
                deliveryEnabled
                  ? nextDayDeliveryOnly
                    ? "내일 배달/배송지로 수령"
                    : "배송지로 수령"
                  : "현재 준비중"
              }
              disabled={!deliveryEnabled}
              onClick={() => deliveryEnabled && setType(ORDER_TYPE.DELIVERY)}
            />
          </div>
        )}
      </section>

      {/* 2. 수령 시간 */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">수령 시간</h2>
        {isNextDayDelivery ? (
          // 다음날 배달 전용: 시간 선택 없이 익일 오전 배송임을 안내한다.
          <p className="rounded-md border border-brand-300 bg-brand-50 p-3 text-sm text-brand-800">
            보냉 포장 후 다음날 오전 중 배송됩니다.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <TypeButton
                active={effectiveMode === "asap"}
                label="가능한 빨리"
                sub={canAsap ? "지금 영업 중" : "지금은 선택 불가"}
                disabled={!canAsap}
                onClick={() => canAsap && setPickupMode("asap")}
              />
              <TypeButton
                active={effectiveMode === "reserve"}
                label="시간 예약"
                sub="일자/시간 선택"
                disabled={reservationDays.length === 0}
                onClick={() => setPickupMode("reserve")}
              />
            </div>

            {effectiveMode === "reserve" ? (
              reservationDays.length === 0 ? (
                <p className="rounded-md border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
                  지금은 예약 가능한 시간이 없습니다. 영업 중에 다시 시도해 주세요.
                </p>
              ) : (
                <div className="flex flex-col gap-2">
                  {/* 날짜 선택 */}
                  <select
                    value={selectedDate}
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      // 날짜가 바뀌면 시·분 선택을 초기화한다.
                      setSelectedHour("");
                      setSelectedMinute("");
                    }}
                    className="h-11 rounded-md border border-ink-300 px-3 text-sm"
                  >
                    <option value="">날짜 선택</option>
                    {reservationDays.map((d) => (
                      <option key={d.yyyyMmDd} value={d.yyyyMmDd}>
                        {formatDayOption(d.yyyyMmDd, d.weekday)}
                      </option>
                    ))}
                  </select>
                  {/* 시각: [시]/[분] 두 칸. 시를 고르면 해당 시의 분만 선택지로 나온다. */}
                  {selectedDate ? (
                    <div className="grid grid-cols-2 gap-2">
                      <select
                        value={selectedHour}
                        onChange={(e) => {
                          setSelectedHour(e.target.value);
                          // 시가 바뀌면 분 선택을 초기화한다(이전 시의 분이 새 시에 없을 수 있음).
                          setSelectedMinute("");
                        }}
                        aria-label="시 선택"
                        className="h-11 rounded-md border border-ink-300 px-3 text-sm tabular-nums"
                      >
                        <option value="">시</option>
                        {availableHours.map((h) => (
                          <option key={h} value={h}>
                            {Number(h)}시
                          </option>
                        ))}
                      </select>
                      <select
                        value={selectedMinute}
                        onChange={(e) => setSelectedMinute(e.target.value)}
                        disabled={!selectedHour}
                        aria-label="분 선택"
                        className="h-11 rounded-md border border-ink-300 px-3 text-sm tabular-nums disabled:bg-ink-100 disabled:text-ink-400"
                      >
                        <option value="">분</option>
                        {availableMinutes.map((m) => (
                          <option key={m} value={m}>
                            {Number(m)}분
                          </option>
                        ))}
                      </select>
                    </div>
                  ) : null}
                </div>
              )
            ) : null}
          </>
        )}
      </section>

      {/* 3. 배송지 (배달 주문만) */}
      {type === ORDER_TYPE.DELIVERY ? (
        <section className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-ink-700">배송지</h2>
            <button
              type="button"
              onClick={() => setAddrModalOpen(true)}
              className="text-xs text-ink-600 underline"
            >
              새 배송지 추가
            </button>
          </div>
          {addressesQuery.isLoading ? (
            <p className="text-sm text-ink-600">불러오는 중...</p>
          ) : addressesQuery.data && addressesQuery.data.length > 0 ? (
            <select
              value={addressId}
              onChange={(e) => setAddressId(e.target.value)}
              className="h-11 rounded-md border border-ink-300 px-3 text-sm"
            >
              <option value="">배송지 선택</option>
              {addressesQuery.data.map((a) => (
                <option key={a.id} value={a.id}>
                  {(a.label ? `[${a.label}] ` : "") + a.addressLine1}
                  {a.addressLine2 ? ` ${a.addressLine2}` : ""}
                </option>
              ))}
            </select>
          ) : (
            <div className="rounded-md border border-dashed border-ink-300 p-4 text-center text-sm text-ink-600">
              <p>등록된 배송지가 없습니다.</p>
              <Button
                type="button"
                size="sm"
                onClick={() => setAddrModalOpen(true)}
                className="mt-2"
              >
                배송지 추가
              </Button>
            </div>
          )}
          {/* 배달 사전 검증 안내 */}
          {selectedAddress && !deliveryAreaOk ? (
            <p className="text-xs text-warning-700">
              선택한 주소는 배달 가능 지역이 아닐 수 있습니다.
            </p>
          ) : null}
          {!deliveryMinOk && settings ? (
            <p className="text-xs text-warning-700">
              배달 최소 주문 금액은 {settings.deliveryMinOrderKrw.toLocaleString()}원입니다.
            </p>
          ) : null}
        </section>
      ) : null}

      {/* 3.5 쿠폰 - 보유 쿠폰이 없어도 섹션은 항상 노출한다(작업지시: 적용 가능한 부분이 보이도록). */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">쿠폰</h2>
        {usableDiscountCoupons.length > 0 ? (
          <>
            <select
              value={discountCouponId}
              onChange={(e) => {
                setDiscountTouched(true);
                setDiscountCouponId(e.target.value);
              }}
              className="h-11 rounded-md border border-ink-300 px-3 text-sm"
            >
              <option value="">할인 쿠폰 미사용</option>
              {usableDiscountCoupons.map((c) => {
                const cond = formatCouponConditions(c);
                return (
                  <option key={c.id} value={c.id}>
                    {c.name} ({formatCouponBenefit(c.type, c.value)})
                    {cond.length > 0 ? ` · ${cond.join(" · ")}` : ""}
                  </option>
                );
              })}
            </select>
            {/* 최소 주문금액 미충족 안내(선택했으나 조건 미달 시). */}
            {selectedDiscountCoupon && !discountMinOrderMet ? (
              <p className="text-xs text-warning-700">
                {(selectedDiscountCoupon.minOrderKrw ?? 0).toLocaleString()}원 이상 주문 시 사용할
                수 있는 쿠폰입니다.
              </p>
            ) : null}
            {/* 자동 적용 안내(개선 1): 사용자가 직접 바꾸기 전, 가장 할인이 큰 쿠폰을 골라둔 상태. */}
            {!discountTouched && discountCouponId ? (
              <p className="text-xs text-brand-700">
                가장 할인이 큰 쿠폰을 자동 적용했어요. 바꿀 수 있습니다.
              </p>
            ) : null}
          </>
        ) : null}
        {type === ORDER_TYPE.DELIVERY && usableDeliveryCoupons.length > 0 ? (
          <>
            <select
              value={deliveryCouponId}
              onChange={(e) => {
                setDeliveryTouched(true);
                setDeliveryCouponId(e.target.value);
              }}
              className="h-11 rounded-md border border-ink-300 px-3 text-sm"
            >
              <option value="">배달비 쿠폰 미사용</option>
              {usableDeliveryCoupons.map((c) => {
                const cond = formatCouponConditions(c);
                return (
                  <option key={c.id} value={c.id}>
                    {c.name} (배달비 무료){cond.length > 0 ? ` · ${cond.join(" · ")}` : ""}
                  </option>
                );
              })}
            </select>
            {selectedDeliveryCoupon && !deliveryMinOrderMet ? (
              <p className="text-xs text-warning-700">
                {(selectedDeliveryCoupon.minOrderKrw ?? 0).toLocaleString()}원 이상 주문 시 사용할
                수 있는 쿠폰입니다.
              </p>
            ) : null}
          </>
        ) : null}
        {usableDiscountCoupons.length === 0 &&
        !(type === ORDER_TYPE.DELIVERY && usableDeliveryCoupons.length > 0) ? (
          <p className="rounded-md border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
            지금 사용할 수 있는 쿠폰이 없습니다.
          </p>
        ) : null}
      </section>

      {/* 3.6 적립금 사용 - 보유 적립금이 없어도 섹션은 항상 노출한다. */}
      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-ink-700">적립금 사용</h2>
          <span className="text-xs text-ink-600">보유 {formatPoints(pointsBalance)}</span>
        </div>
        {pointsBalance > 0 && payableBeforePoints > 0 && usablePoints > 0 ? (
          <>
            <div className="flex gap-2">
              <input
                type="number"
                min={0}
                max={usablePoints}
                step={pointsPolicy.unitKrw}
                value={pointsInput}
                onChange={(e) => setPointsInput(Number.parseInt(e.target.value, 10) || 0)}
                placeholder="사용할 적립금"
                className="h-11 min-w-0 flex-1 rounded-md border border-ink-300 px-3 text-sm tabular-nums"
              />
              <button
                type="button"
                onClick={() => setPointsInput(usablePoints)}
                className="h-11 shrink-0 rounded-md border border-ink-400 px-3 text-sm font-semibold text-ink-700 hover:bg-ink-100"
              >
                전액 사용
              </button>
            </div>
            {/* 최소사용/단위 정책 안내 (제한이 있을 때만) */}
            {pointsPolicy.minKrw > 0 || pointsPolicy.unitKrw > 1 ? (
              <p className="text-xs text-ink-600">
                {pointsPolicy.minKrw > 0 ? `${formatPoints(pointsPolicy.minKrw)} 이상` : null}
                {pointsPolicy.minKrw > 0 && pointsPolicy.unitKrw > 1 ? " · " : null}
                {pointsPolicy.unitKrw > 1
                  ? `${pointsPolicy.unitKrw.toLocaleString()}원 단위`
                  : null}{" "}
                사용할 수 있어요. (최대 {formatPoints(usablePoints)})
              </p>
            ) : null}
            {/* 입력값이 정책에 어긋나면 주문 버튼이 잠기므로 사유를 안내한다. */}
            {!pointsInputValid ? (
              <p className="text-xs text-warning-700">
                {pointsInput > Math.min(pointsBalance, payableBeforePoints)
                  ? `최대 ${formatPoints(usablePoints)}까지 사용할 수 있습니다.`
                  : `${pointsPolicy.minKrw > 0 ? `${formatPoints(pointsPolicy.minKrw)} 이상, ` : ""}${pointsPolicy.unitKrw}원 단위로 입력해 주세요.`}
              </p>
            ) : null}
          </>
        ) : (
          <p className="rounded-md border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
            {pointsBalance === 0
              ? "사용할 수 있는 적립금이 없습니다."
              : pointsPolicy.minKrw > 0 && pointsBalance < pointsPolicy.minKrw
                ? `적립금은 ${formatPoints(pointsPolicy.minKrw)} 이상 모이면 사용할 수 있어요.`
                : "이 주문에는 적립금을 사용할 수 없습니다."}
          </p>
        )}
      </section>

      {/* 3.7 칭찬하고 할인받기(개선 9~12) - 운영설정에서 켜져 있을 때만 노출 */}
      {complimentState?.enabled ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-ink-700">칭찬하고 할인받기</h2>
          {complimentState.locked ? (
            // 이미 칭찬을 골라 잠긴 상태. 고른 칭찬 + 할인액 표시(주문 종결 전까지 고정).
            complimentState.appliedToOrder ? (
              <p className="rounded-md border border-ink-200 bg-ink-50 p-3 text-xs text-ink-600">
                칭찬 할인은 진행 중인 주문에 이미 적용되어 있습니다. 그 주문이 마무리되면 다시
                참여할 수 있어요.
              </p>
            ) : (
              <div className="rounded-md border border-brand-300 bg-brand-50 p-3">
                <p className="text-sm font-medium text-brand-800">
                  {complimentState.complimentText}
                </p>
                <p className="mt-0.5 text-xs text-brand-700">
                  칭찬 감사 할인 {complimentState.discountKrw.toLocaleString()}원이 적용되었어요.
                </p>
              </div>
            )
          ) : !complimentExpanded ? (
            // 접힌 상태: 누르면 무작위 칭찬 후보가 펼쳐진다.
            <button
              type="button"
              onClick={() => setComplimentExpanded(true)}
              className="rounded-md border border-brand-300 bg-white p-3 text-left text-sm text-brand-700 hover:bg-brand-50"
            >
              칭찬을 남기고 할인 받기 (눌러서 칭찬 고르기)
            </button>
          ) : (
            // 펼친 상태: 후보 칭찬을 체크박스로 노출. 하나만 고를 수 있고, 고르면 할인이 적용된다.
            <div className="flex flex-col gap-2">
              <p className="text-xs text-ink-600">
                칭찬 하나를 고르면 감사 할인이 무작위로 적용됩니다. 한 번 고르면 변경할 수 없어요.
              </p>
              <ul className="flex flex-col gap-1.5">
                {complimentState.options.map((opt) => (
                  <li key={opt}>
                    <button
                      type="button"
                      disabled={complimentSelectMut.isPending}
                      onClick={() => complimentSelectMut.mutate(opt)}
                      className="flex w-full items-center gap-2 rounded-md border border-ink-300 bg-white p-3 text-left text-sm hover:bg-ink-50 disabled:opacity-50"
                    >
                      <span
                        className="inline-block h-4 w-4 shrink-0 rounded border border-ink-400"
                        aria-hidden="true"
                      />
                      <span className="min-w-0 flex-1">{opt}</span>
                    </button>
                  </li>
                ))}
              </ul>
              {complimentSelectMut.isError ? (
                <p className="text-xs text-warning-700">잠시 후 다시 시도해 주세요.</p>
              ) : null}
            </div>
          )}
        </section>
      ) : null}

      {/* 4. 요청사항 */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">요청사항 (선택)</h2>
        <textarea
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
          maxLength={500}
          rows={3}
          placeholder="예: 다져주세요, 깍뚝 썰어주세요"
          className="rounded-md border border-ink-300 p-3 text-sm"
        />
      </section>

      {/* 4.5 결제 수단. 실중량 관리 대상품이 담기면 정산 가능한 수단 하나만 남는다(선택지 없음). */}
      <section className="flex flex-col gap-2">
        <h2 className="text-sm font-semibold text-ink-700">결제 수단</h2>
        {hasWeightAdjustableItem ? (
          // 중량 변동 상품 안내: 왜 카드 즉시결제를 못 쓰는지 + 예상 금액 범위.
          <div className="rounded-md border border-warning-600/30 bg-warning-50 p-3 text-xs text-warning-700">
            <p className="font-semibold">
              {weightAdjustableItems.map((it) => it.productSnapshot.name).join(", ")}
            </p>
            <p className="mt-1">
              위 상품은 자투리 고기 발생을 예방하고자 포장 시 중량이 조금 늘거나 줄 수 있어, 최종
              금액이 약{" "}
              <span className="font-semibold tabular-nums">{minPayment.toLocaleString()}원</span> ~{" "}
              <span className="font-semibold tabular-nums">{maxPayment.toLocaleString()}원</span>{" "}
              범위에서 확정됩니다. 그래서{" "}
              <span className="font-semibold">
                {type === ORDER_TYPE.DELIVERY ? "문자(SMS) 결제" : "현장 결제"}
              </span>
              로만 주문하실 수 있습니다.
            </p>
          </div>
        ) : null}
        <div className={`grid gap-2 ${allowedMethods.length > 1 ? "grid-cols-2" : "grid-cols-1"}`}>
          {allowedMethods.map((m) => (
            <TypeButton
              key={m}
              active={paymentMethodType === m}
              label={PAYMENT_METHOD_TYPE_LABEL_KO[m]}
              sub={paymentMethodSubLabel(m)}
              onClick={() => setPaymentMethodType(m)}
            />
          ))}
        </div>

        {/* 일반(즉시) 카드결제: 주문 시 결제창에서 표시 금액 그대로 결제한다(가격 고정). */}
        {paymentMethodType === PAYMENT_METHOD_TYPE.INAPP_DIRECT ? (
          <p className="text-xs text-ink-600">
            주문하기를 누르면 카드 결제창이 열립니다. 표시된 결제 예정 금액으로 바로 결제됩니다.
          </p>
        ) : null}
      </section>

      {/* 5. 주문 요약 */}
      <section className="flex flex-col gap-2 rounded-lg border border-ink-200 bg-ink-50 p-4">
        <h2 className="text-sm font-semibold text-ink-700">주문 상품</h2>
        <ul className="flex flex-col gap-1.5">
          {view.items.map((it) => (
            <CheckoutItemLine key={it.id} item={it} units={units} />
          ))}
        </ul>
        <div className="mt-2 border-t border-ink-200 pt-2 text-sm">
          {/* 총 중량(기본+추가옵션). 중량을 알 수 없는 상품만 담겼으면 표시하지 않는다. */}
          {totalWeightG > 0 ? <Row label="총 중량" value={formatWeightG(totalWeightG)} /> : null}
          <Row label="상품 합계" value={`${subtotal.toLocaleString()}원`} />
          {discount > 0 ? (
            <Row label="쿠폰 할인" value={`-${discount.toLocaleString()}원`} />
          ) : null}
          {complimentDiscount > 0 ? (
            <Row label="칭찬 감사 할인" value={`-${complimentDiscount.toLocaleString()}원`} />
          ) : null}
          {pointsUsed > 0 ? (
            <Row label="적립금 사용" value={`-${pointsUsed.toLocaleString()}원`} />
          ) : null}
          {type === ORDER_TYPE.DELIVERY ? (
            <Row
              label="배달비"
              value={deliveryCouponApplied ? "무료" : `${deliveryFee.toLocaleString()}원`}
            />
          ) : null}
          <div className="mt-1 flex items-baseline justify-between">
            <span className="text-base font-semibold">결제 예정 금액</span>
            <span className="text-2xl font-bold tabular-nums">{total.toLocaleString()}원</span>
          </div>
        </div>
        <p className="text-xs text-ink-600">{paymentSummaryNote}</p>
      </section>

      {errorMsg ? (
        <p className="rounded-md border border-warning-600/30 bg-warning-50 p-3 text-sm text-warning-700">
          {errorMsg}
        </p>
      ) : null}

      {/* 하단 고정 주문 버튼 */}
      <div className="fixed inset-x-0 bottom-[calc(4rem+var(--safe-bottom))] z-10 border-t border-ink-200 bg-surface px-4 py-3">
        <div className="mx-auto max-w-screen-sm">
          <Button
            type="button"
            size="lg"
            fullWidth
            onClick={() => {
              setErrorMsg(null);
              setAgreeError(false);
              setConfirmOpen(true);
            }}
            disabled={!canSubmit}
            loading={submitting}
          >
            {submitting ? "접수 중..." : `${total.toLocaleString()}원 주문하기`}
          </Button>
        </div>
      </div>

      {/* 주문 확인 모달(개선 2): 주문내역 요약을 보여주고 '확인'을 눌러야 실제 접수된다. */}
      {confirmOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-ink-900/50 p-0 sm:items-center sm:p-4"
          onClick={() => {
            if (!submitting) setConfirmOpen(false);
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-confirm-title"
            className="flex max-h-[85vh] w-full max-w-screen-sm flex-col overflow-y-auto rounded-t-2xl bg-surface p-5 pb-[calc(1.25rem+var(--safe-bottom))] shadow-elevated sm:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="checkout-confirm-title" className="text-lg font-bold text-ink-900">
              주문 내용을 확인해 주세요
            </h2>

            {/* 수령 방식/시간/배송지 요약 */}
            <dl className="mt-3 flex flex-col gap-1 rounded-md border border-ink-200 bg-ink-50 p-3 text-sm">
              <Row label="주문 방식" value={type === ORDER_TYPE.DELIVERY ? "배달" : "포장"} />
              <Row
                label="수령 시간"
                value={
                  isNextDayDelivery
                    ? "다음날 오전 중 배송"
                    : effectiveMode === "asap"
                      ? "가능한 빨리"
                      : formatDayOption(
                          selectedDate,
                          reservationDays.find((d) => d.yyyyMmDd === selectedDate)?.weekday ??
                            "mon",
                        ) + ` ${selectedTime}`
                }
              />
              {type === ORDER_TYPE.DELIVERY && selectedAddress ? (
                <Row
                  label="배송지"
                  value={`${selectedAddress.addressLine1}${selectedAddress.addressLine2 ? ` ${selectedAddress.addressLine2}` : ""}`}
                />
              ) : null}
              {memo.trim() ? <Row label="요청사항" value={memo.trim()} /> : null}
            </dl>

            {/* 품목 + 금액 요약 */}
            <ul className="mt-2 flex flex-col gap-1.5 rounded-md bg-ink-50 p-3 text-sm">
              {view.items.map((it) => (
                <CheckoutItemLine key={it.id} item={it} units={units} />
              ))}
              <li className="mt-1 border-t border-ink-200 pt-1">
                {totalWeightG > 0 ? (
                  <Row label="총 중량" value={formatWeightG(totalWeightG)} />
                ) : null}
                <Row label="상품 합계" value={`${subtotal.toLocaleString()}원`} />
              </li>
              {discount > 0 ? (
                <Row label="쿠폰 할인" value={`-${discount.toLocaleString()}원`} />
              ) : null}
              {complimentDiscount > 0 ? (
                <Row label="칭찬 감사 할인" value={`-${complimentDiscount.toLocaleString()}원`} />
              ) : null}
              {pointsUsed > 0 ? (
                <Row label="적립금 사용" value={`-${pointsUsed.toLocaleString()}원`} />
              ) : null}
              {type === ORDER_TYPE.DELIVERY ? (
                <Row
                  label="배달비"
                  value={deliveryCouponApplied ? "무료" : `${deliveryFee.toLocaleString()}원`}
                />
              ) : null}
              <li className="mt-1 flex items-baseline justify-between border-t border-ink-200 pt-1 font-semibold">
                <span>결제 예정 금액</span>
                <span className="text-lg tabular-nums">{total.toLocaleString()}원</span>
              </li>
            </ul>

            {/* 다음날 배달 전용 안내(배달 주문일 때). 보냉 포장 + 익일 오전 배송임을 명확히 한다. */}
            {isNextDayDelivery ? (
              <p className="mt-3 rounded-md border border-brand-300 bg-brand-50 p-3 text-sm font-medium text-brand-800">
                상품 보냉 포장 후 다음날 오전 중 배송됩니다.
              </p>
            ) : null}

            {/* 안내 사항(개선 3): 두 가지 안내 + 확인 체크박스. 체크 안하면 주문 확인이 막히고 빨갛게 강조된다. */}
            <ul className="mt-3 list-disc space-y-1 pl-5 text-xs text-ink-700">
              <li>{paymentSummaryNote}</li>
              {/* 실중량 관리 대상품: 포장 중량 변동으로 최종 금액이 범위 안에서 확정되고, 결제수단이 제한된다. */}
              {hasWeightAdjustableItem ? (
                <li>
                  <span className="font-semibold">
                    {weightAdjustableItems.map((it) => it.productSnapshot.name).join(", ")}
                  </span>{" "}
                  상품은 자투리 고기 발생을 예방하고자 포장 시 중량 상승에 대한 가격 변동이 있어,
                  실제 결제 금액은 약{" "}
                  <span className="font-semibold tabular-nums">
                    {minPayment.toLocaleString()}원
                  </span>{" "}
                  ~{" "}
                  <span className="font-semibold tabular-nums">
                    {maxPayment.toLocaleString()}원
                  </span>{" "}
                  범위에서 확정되며, {type === ORDER_TYPE.DELIVERY ? "문자(SMS) 결제" : "현장 결제"}
                  로만 결제 가능합니다.
                </li>
              ) : null}
              {/* 실중량 대상은 아니지만 무게로 파는 상품: 표시 금액 그대로 결제되며 자투리가 섞일 수 있다. */}
              {weightUnitItems.length > 0 ? (
                <li>
                  <span className="font-semibold">
                    {weightUnitItems.map((it) => it.productSnapshot.name).join(", ")}
                  </span>{" "}
                  상품은 중량을 맞추기 위해 일부 자투리가 포함될 수 있습니다.
                </li>
              ) : null}
            </ul>
            <label
              className={`mt-3 flex cursor-pointer items-start gap-2 rounded-md border p-3 text-sm ${
                agreeError && !agreed
                  ? "border-danger-500 bg-danger-50 text-danger-700"
                  : "border-ink-300 bg-surface text-ink-800"
              }`}
            >
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => {
                  setAgreed(e.target.checked);
                  if (e.target.checked) setAgreeError(false);
                }}
                className="mt-0.5 h-4 w-4 shrink-0"
              />
              <span className="font-medium">안내 사항을 확인하셨습니까?</span>
            </label>

            {/* 하단 버튼: [이전] 은 좁게 고정, [주문 확인] 이 남은 폭을 차지해 한 줄로 깔끔하게. */}
            <div className="mt-4 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
                className="inline-flex min-h-touch shrink-0 items-center justify-center whitespace-nowrap rounded-md border border-ink-300 bg-surface px-5 text-base font-semibold text-ink-900 hover:bg-ink-50 disabled:opacity-50"
              >
                이전
              </button>
              <Button
                type="button"
                fullWidth
                onClick={() => {
                  // 안내 확인 미체크면 강조만 하고 진행하지 않는다.
                  if (!agreed) {
                    setAgreeError(true);
                    return;
                  }
                  onSubmit();
                }}
                loading={submitting}
                disabled={submitting}
              >
                {submitting ? "접수 중..." : "주문 확인"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* 인라인 배송지 추가 모달 - 생성 즉시 선택값으로 설정 */}
      {addrModalOpen ? (
        <AddressQuickAddModal
          onClose={() => setAddrModalOpen(false)}
          onCreated={async (addr) => {
            await qc.invalidateQueries({ queryKey: ["addresses"] });
            setAddressId(addr.id);
            setAddrModalOpen(false);
          }}
        />
      ) : null}
    </main>
  );
}

// 'YYYY-MM-DD' + weekday -> 'M/D (요일)' 옵션 라벨.
function formatDayOption(yyyyMmDd: string, weekday: keyof typeof WEEKDAY_LABEL_KO): string {
  const [, mm, dd] = yyyyMmDd.split("-");
  return `${Number(mm)}/${Number(dd)} (${WEEKDAY_LABEL_KO[weekday]})`;
}

// 결제 수단 버튼의 보조 설명(손님 친화 카피).
function paymentMethodSubLabel(m: PaymentMethodType): string {
  switch (m) {
    case PAYMENT_METHOD_TYPE.INAPP_DIRECT:
      return "카드 결제창에서 즉시 결제";
    case PAYMENT_METHOD_TYPE.ONSITE:
      return "매장 수령 시 현장 결제";
    case PAYMENT_METHOD_TYPE.SMS_REQUEST:
      return "결제 안내 문자 받기";
    default:
      return "";
  }
}

// 주문 상품 한 줄(요약 목록과 확인 모달이 공유). 추가 옵션이 있으면 하위 줄로 함께 보여준다.
//  - 옵션 금액은 lineTotalKrw 에 이미 합산돼 있어 우측 금액은 줄 전체 합계다.
function CheckoutItemLine({ item, units }: { item: CartItemDto; units: UnitDto[] }) {
  return (
    <li className="flex flex-col gap-0.5 text-sm">
      <div className="flex items-baseline justify-between gap-2">
        <span className="min-w-0 flex-1 truncate text-ink-800">
          {item.productSnapshot.name}{" "}
          <span className="text-xs text-ink-600">
            ({formatCartLineLabel(units, item.productSnapshot.unit, item.quantity)})
          </span>
        </span>
        <span className="shrink-0 tabular-nums text-ink-800">
          {item.lineTotalKrw.toLocaleString()}원
        </span>
      </div>
      {item.options.map((o) => (
        <div key={o.optionId} className="pl-3 text-xs text-ink-600">
          + {o.label} x{o.quantity}
          {o.addGrams > 0 ? ` (${formatWeightG(o.addGrams * o.quantity)})` : ""}
        </div>
      ))}
      {item.totalWeightG ? (
        <div className="pl-3 text-xs text-ink-500">총 {formatWeightG(item.totalWeightG)}</div>
      ) : null}
    </li>
  );
}

// 요약 행.
function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline justify-between py-0.5 text-ink-700">
      <span>{label}</span>
      <span className="tabular-nums">{value}</span>
    </div>
  );
}

// 토글 버튼 (주문 방식/수령 모드 공용).
function TypeButton({
  active,
  label,
  sub,
  disabled,
  onClick,
}: {
  active: boolean;
  label: string;
  sub: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`flex min-h-touch flex-col items-start rounded-lg border p-3 text-left ${
        active
          ? "border-brand-600 bg-brand-600 text-white"
          : "border-ink-300 bg-surface text-ink-900 hover:bg-ink-50"
      } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
    >
      <span className="text-base font-semibold">{label}</span>
      <span className={`text-xs ${active ? "text-brand-100" : "text-ink-600"}`}>{sub}</span>
    </button>
  );
}
